import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    TextField, Stack, Typography, Checkbox, FormControlLabel, Box, Alert,
    Button, Slider, Card, CardContent,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import MyScatterChart from "../../components/MyScatterChart";
import LoadingComponent from "../../components/LoadingComponent";
import {
    selectionSidebar, selectionLockedRoute, isImageSelection,
} from "../../helper/selectionFlow";
import { useSelectionParams } from "../../helper/entityRoutes";

/** Patches per repeatability batch: the camera field is 10 x 10 patches. */
const BATCH_SIZE = 100;

/** "500 samples -> 5 repeatability batches of up to 100 patches (10 x 10)". */
export function batchHelperText(totalSamples) {
    const total = Number(totalSamples);
    if (!Number.isFinite(total) || total <= 0) return `Marked and photographed in batches of up to ${BATCH_SIZE} patches (10 x 10).`;
    const batches = Math.ceil(total / BATCH_SIZE);
    const remainder = total % BATCH_SIZE;
    if (batches === 1) return `One repeatability batch of ${total} patch${total === 1 ? "" : "es"}.`;
    return `${batches} repeatability batches: ${batches - (remainder ? 1 : 0)} x ${BATCH_SIZE} patches (10 x 10)${remainder ? ` + ${remainder}` : ""}.`;
}

/** Blacks and whites of an achromatic quota at a black share in percent:
 *  half a sample goes to black, so 50 % gives an odd quota's extra sample to
 *  black; 100 % is blacks only, 0 % whites only (backend split_achromatic). */
export function splitAchromatic(achromatic, blackRatio) {
    const black = Math.min(Math.max(Math.floor((achromatic * blackRatio) / 100 + 0.5), 0), achromatic);
    return { black, white: achromatic - black };
}

// Step 4: compose the final palette out of the pruned pool. The user picks
// the total sample count, whether chromatic and/or achromatic samples are
// included, the chromatic:achromatic ratio, the black:white ratio of the
// achromatic share, the minimum chromaticity a chromatic sample must exceed
// and the maximum chromaticity of a black or white. The achromatic quota is
// split by the black:white ratio between the best blacks and the best
// whites (closest to L* 0 / L* 100) within that maximum. The composed palette
// is marked for the repeatability test in batches of at most 100 patches
// (one 10 x 10 camera field); the server reports the batch layout.
export default function ColorSelectionComposition() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [includeChromatic, setIncludeChromatic] = useState(true);
    const [includeAchromatic, setIncludeAchromatic] = useState(true);
    const [totalSamples, setTotalSamples] = useState(49);
    const [chromaticRatio, setChromaticRatio] = useState(90);
    const [blackRatio, setBlackRatio] = useState(50);
    const [chromaThreshold, setChromaThreshold] = useState(5);
    const [achromaticChromaLimit, setAchromaticChromaLimit] = useState(5);
    const [composedColor, setComposedColor] = useState(null);
    const [composition, setComposition] = useState(null);
    const [applied, setApplied] = useState(false);

    const { selectionId, selectionType, stepRoute } = useSelectionParams();
    // The image selection ranks the filtered samples by chromaticity and keeps
    // the best N (chromatic only; its black and white come per width group).
    const imageType = isImageSelection(selectionType);
    useEffect(() => {
        if (imageType) {
            setIncludeAchromatic(false);
            setTotalSamples(100);
            setChromaThreshold(15);
        }
    }, [imageType]);

    useEffect(() => {
        // Finished (or repeatability-confirmed) selections: composing again is forbidden.
        axios.get(`api/color/selection/${selectionId}/`)
            .then((res) => {
                const locked = selectionLockedRoute(
                    selectionId, res.data.status, res.data.selection_type || selectionType,
                );
                if (locked) {
                    navigate(locked, { replace: true });
                    return;
                }
                try {
                    // The last used chromaticity limits and black:white ratio (seeded with the defaults).
                    const config = JSON.parse(res.data.selection_config);
                    if (config.chromaticity_threshold != null) setChromaThreshold(config.chromaticity_threshold);
                    if (config.max_achromatic_chromaticity != null) setAchromaticChromaLimit(config.max_achromatic_chromaticity);
                    if (Number.isFinite(Number(config.black_ratio)) && config.black_ratio != null) {
                        setBlackRatio(Math.min(Math.max(Number(config.black_ratio), 0), 100));
                    }
                    if (config.n_best != null && isImageSelection(res.data.selection_type)) setTotalSamples(config.n_best);
                } catch (e) {
                    console.error("Could not parse selection config", e);
                }
            })
            .catch((err) => console.error("Error checking selection status:", err));
    }, []);

    const bothIncluded = includeChromatic && includeAchromatic;
    const total = Math.max(0, parseInt(totalSamples, 10) || 0);
    // The backend rejects totals below 1; any count from 1 up is a valid
    // palette (small logo palettes are legitimate).
    const totalTooSmall = total < 1;
    const invalidLimit = (raw) => String(raw).trim() === "" || !Number.isFinite(Number(raw)) || Number(raw) < 0;
    const threshold = Number(chromaThreshold);
    const thresholdInvalid = includeChromatic && invalidLimit(chromaThreshold);
    const achromaticLimit = Number(achromaticChromaLimit);
    const achromaticLimitInvalid = includeAchromatic && invalidLimit(achromaticChromaLimit);
    const cannotCompose = loading || totalTooSmall || thresholdInvalid || achromaticLimitInvalid
        || (!includeChromatic && !includeAchromatic);
    let previewChromatic = 0;
    if (includeChromatic) {
        previewChromatic = bothIncluded ? Math.round((total * chromaticRatio) / 100) : total;
    }
    const previewAchromatic = total - previewChromatic;
    const { black: previewBlack, white: previewWhite } = splitAchromatic(previewAchromatic, blackRatio);

    const applyComposition = async () => {
        setLoading(true);
        setErrorMessage(null);
        try {
            const response = await axios.post("api/selection/compose", {
                selectionId,
                includeChromatic,
                includeAchromatic,
                totalSamples: total,
                chromaticRatio,
                blackRatio,
                // A limit of an unticked kind is not sent (the server keeps the stored one).
                chromaticityThreshold: includeChromatic ? threshold : null,
                maxAchromaticChromaticity: includeAchromatic ? achromaticLimit : null,
            });
            if (response.data && response.data.Success) {
                setComposedColor(response.data.color || []);
                setComposition(response.data.composition);
                setApplied(true);
                return true;
            }
            setErrorMessage((response.data && response.data.error) || "Composing the selection failed.");
            return false;
        } catch (error) {
            const detail = error.response && error.response.data && error.response.data.error;
            console.error("Error composing selection:", error);
            setErrorMessage(detail || "Composing the selection failed.");
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Next composes with the current settings (re-composing always restarts
    // from the pruned pool server-side) and only navigates on success.
    const nextPageClickFunc = async (navigatePageName) => {
        const ok = await applyComposition();
        if (ok) {
            navigate(navigatePageName);
        }
    };

    return (
        <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
            <ExplorationPageShell
                title={imageType ? "Ranking" : "Composition"}
                subtitle={imageType
                    ? "Rank the filtered samples by chromaticity and keep the best N."
                    : "Choose how many samples the palette should hold and how chromatic and achromatic samples are balanced."}
                stepSidebar={selectionSidebar(3, applied && composition
                    ? composition.chromatic + composition.black + composition.white
                    : null, selectionType)}
                footerBelowPaper={
                    <Box sx={{ width: "100%" }}>
                        <NavBarBottom
                            variant="embedded"
                            returnLabel="Back"
                            prevPage={stepRoute("pruning")}
                            nextPage={stepRoute("prepass")}
                            enableNextPage={cannotCompose}
                            nextPageClickFunc={nextPageClickFunc}
                        />
                    </Box>
                }
            >
                <Stack spacing={3}>
                    {loading && <LoadingComponent message="Composing the selection..." />}
                    {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

                    <Stack direction={{ xs: "column", lg: "row" }} spacing={3} alignItems="stretch">
                        <Card variant="outlined" sx={{ borderRadius: 3, flexShrink: 0, minWidth: 380 }}>
                            <CardContent>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                                    {imageType ? "Ranking" : "Palette composition"}
                                </Typography>
                                <Stack spacing={2}>
                                    <Stack direction="column" sx={imageType ? { display: "none" } : undefined}>
                                        <FormControlLabel
                                            control={<Checkbox checked={includeChromatic} onChange={(e) => setIncludeChromatic(e.target.checked)} />}
                                            label="Chromatic samples"
                                        />
                                        <FormControlLabel
                                            control={<Checkbox checked={includeAchromatic} onChange={(e) => setIncludeAchromatic(e.target.checked)} />}
                                            label="Achromatic samples (best blacks and whites)"
                                        />
                                    </Stack>
                                    <TextField
                                        label="Min. chromaticity (C*)"
                                        type="number"
                                        value={chromaThreshold}
                                        onChange={(e) => setChromaThreshold(e.target.value)}
                                        inputProps={{ min: 0, step: 1 }}
                                        disabled={!includeChromatic}
                                        error={thresholdInvalid}
                                        helperText={thresholdInvalid ? "Enter a value of 0 or more." : "Applies to chromatic samples only."}
                                        fullWidth
                                    />
                                    {!imageType && <TextField
                                        label="Max. chromaticity (C*)"
                                        type="number"
                                        value={achromaticChromaLimit}
                                        onChange={(e) => setAchromaticChromaLimit(e.target.value)}
                                        inputProps={{ min: 0, step: 0.5 }}
                                        disabled={!includeAchromatic}
                                        error={achromaticLimitInvalid}
                                        helperText={achromaticLimitInvalid ? "Enter a value of 0 or more." : "Applies to blacks and whites only."}
                                        fullWidth
                                    />}
                                    <TextField
                                        label={imageType ? "Number of best samples (N)" : "Total number of samples"}
                                        type="number"
                                        value={totalSamples}
                                        onChange={(e) => setTotalSamples(e.target.value)}
                                        inputProps={{ min: 1, step: 1 }}
                                        error={totalTooSmall}
                                        helperText={totalTooSmall ? "Enter at least 1 sample." : batchHelperText(totalSamples)}
                                        fullWidth
                                    />
                                    <Box sx={imageType ? { display: "none" } : undefined}>
                                        <Typography gutterBottom variant="body2" color={bothIncluded ? "text.primary" : "text.disabled"}>
                                            Chromatic : achromatic ratio — {chromaticRatio}% : {100 - chromaticRatio}%
                                        </Typography>
                                        <Slider
                                            value={chromaticRatio}
                                            onChange={(e, value) => setChromaticRatio(value)}
                                            min={0}
                                            max={100}
                                            step={5}
                                            disabled={!bothIncluded}
                                            valueLabelDisplay="auto"
                                            aria-label="Chromatic : achromatic ratio"
                                        />
                                    </Box>
                                    <Box sx={imageType ? { display: "none" } : undefined}>
                                        <Typography gutterBottom variant="body2" color={includeAchromatic ? "text.primary" : "text.disabled"}>
                                            Black : white ratio — {blackRatio}% : {100 - blackRatio}%
                                        </Typography>
                                        <Slider
                                            value={blackRatio}
                                            onChange={(e, value) => setBlackRatio(value)}
                                            min={0}
                                            max={100}
                                            step={5}
                                            disabled={!includeAchromatic}
                                            valueLabelDisplay="auto"
                                            aria-label="Black : white ratio"
                                        />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        {imageType
                                            ? `This keeps the ${previewChromatic} best samples above the minimum chromaticity.`
                                            : `This selects ${previewChromatic} chromatic, ${previewBlack} black and ${previewWhite} white samples.`}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        startIcon={<AutoFixHighIcon />}
                                        onClick={applyComposition}
                                        disabled={cannotCompose}
                                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                                    >
                                        Preview composition
                                    </Button>
                                    <Typography variant="caption" color="text.secondary">
                                        Next composes the palette with these settings — the preview is optional.
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </Card>

                        <Stack spacing={2} sx={{ flex: 1 }} alignItems="center" justifyContent="flex-start">
                            {applied && composition ? (
                                <>
                                    <Alert severity="success" sx={{ width: "100%" }}>
                                        {`Composed ${composition.chromatic} chromatic, ${composition.black} black and `}
                                        {(composition.batches?.length ?? 1) > 1
                                            ? `${composition.white} white samples — ${composition.batches.length} repeatability batches: ${composition.batches.map((b) => `${b.count} (${b.grid_size[0]} x ${b.grid_size[1]})`).join(", ")}.`
                                            : `${composition.white} white samples — marking grid ${composition.grid[0]} x ${composition.grid[1]}.`}
                                    </Alert>
                                    <MyScatterChart dataColor={composedColor || []} width={420} height={340} />
                                    {(composition.black > 0 || composition.white > 0) && (
                                        <Box sx={{ width: "100%" }}>
                                            <Typography variant="caption" color="text.secondary">
                                                The achromatic samples sit at the a*/b* origin of the plot above, so
                                                they are shown separately here:
                                            </Typography>
                                            {[
                                                { label: "Blacks", rows: composition.blacks || [] },
                                                { label: "Whites", rows: composition.whites || [] },
                                            ].filter((group) => group.rows.length > 0).map((group) => (
                                                <Stack
                                                    key={group.label}
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    flexWrap="wrap"
                                                    useFlexGap
                                                    sx={{ mt: 1 }}
                                                >
                                                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 84 }}>
                                                        {`${group.label} (${group.rows.length})`}
                                                    </Typography>
                                                    {group.rows.map((row, i) => (
                                                        <Box
                                                            key={i}
                                                            title={`L* = ${row.l_star}`}
                                                            sx={{
                                                                width: 26,
                                                                height: 26,
                                                                borderRadius: 0.75,
                                                                bgcolor: `rgb(${row.color.join(",")})`,
                                                                border: "1px solid rgba(15, 23, 42, 0.25)",
                                                            }}
                                                        />
                                                    ))}
                                                </Stack>
                                            ))}
                                        </Box>
                                    )}
                                </>
                            ) : (
                                <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
                                    <Typography>
                                        Press Next to compose the palette with these settings, or preview it here first.
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
            </ExplorationPageShell>
        </Stack>
    );
}
